/**
 * The score, and its working.
 *
 * The competing product ships one number with no way to see inside it. This
 * renders the number and then immediately renders why — every component's
 * inputs, in the trader's own units, next to the points it contributed.
 *
 * That is not a nicety. A score a trader cannot interrogate is a claim, and
 * `docs/08` already forbids AEGIS from making claims it will not show working
 * for. The same standard applies whether the claim came from a model or from
 * arithmetic.
 */
import type { JSX } from 'react';
import type { ScoreResponse, ScenariosResponse } from '@aegis/api-client';
import { Icon } from './Icon.js';

const money = (s: string): string => {
  const n = Number(s);
  return `${n < 0 ? '−' : n > 0 ? '+' : ''}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

export function ScorePanel({ score }: { score: ScoreResponse | null }): JSX.Element | null {
  if (!score) return null;
  const total = Number(score.total);

  return (
    <section className="score-panel" aria-labelledby="score-h">
      <header className="score-head">
        <div>
          <h3 id="score-h">Process score</h3>
          <p className="dim">
            How you traded, not how much you made. Four parts, each showing its own working.
          </p>
        </div>
        {/* Deliberately not coloured by value. A red 38 reads as a verdict, and
            this is a description of a period, not a grade. */}
        <div className="score-total">
          <span className="score-number">{total.toFixed(0)}</span>
          <span className="score-denom">/ 100</span>
        </div>
      </header>

      {score.sampleWarning && (
        <p className="score-warning">
          <Icon name="alerts" /> {score.sampleWarning}
        </p>
      )}

      <div className="score-components">
        {score.components.map((c) => (
          <article key={c.id} className="score-component">
            <header>
              <h4>{c.label}</h4>
              <span className="score-points">{Number(c.points).toFixed(1)}<small> / 25</small></span>
            </header>
            {/* The meter is a static width, never a transition — a component
                easing toward its value displays numbers that were never true.
                See stillSurfaces in @aegis/ui. */}
            <div className="meter" role="img"
                 aria-label={`${c.label}: ${Number(c.points).toFixed(1)} of 25`}>
              <i style={{ width: `${Number(c.ratio) * 100}%` }} />
            </div>
            <p className="score-reading">{c.reading}</p>
            <dl className="score-inputs">
              {c.inputs.map((i) => (
                <div key={i.label}>
                  <dt>{i.label}</dt>
                  <dd>{i.value}</dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>

      <p className="score-footnote dim">
        This describes a period that has already happened. It is not a forecast, and a high
        score is not evidence the next trade will work.
      </p>
    </section>
  );
}

/**
 * Performance by the market regime a trade opened in.
 *
 * The competitor's "scenarios" are pre-built practice setups against history.
 * These are the trader's own trades, grouped by what the market was actually
 * doing when they took them — which is only possible because AEGIS classifies
 * the regime live and stamps it at the time.
 */
export function ScenarioPanel({ s }: { s: ScenariosResponse | null }): JSX.Element | null {
  if (!s || s.scenarios.length === 0) return null;
  const classified = s.scenarios.filter((x) => x.regime !== 'unclassified');
  const unclassified = s.scenarios.find((x) => x.regime === 'unclassified');

  return (
    <section className="scenario-panel" aria-labelledby="scenario-h">
      <header>
        <h3 id="scenario-h">By market condition</h3>
        <p className="dim">
          Your trades, grouped by what the market was doing when each one opened.
        </p>
      </header>

      {classified.length === 0 ? (
        <p className="dim">
          Nothing classified yet. The market condition is recorded as a trade opens, so this
          fills in from your next session rather than from history — a regime applied
          after the fact would be today's read on a market that has moved on.
        </p>
      ) : (
        <table className="scenario-table">
          <thead>
            <tr>
              <th scope="col">Condition</th>
              <th scope="col">Trades</th>
              <th scope="col">Share</th>
              <th scope="col">Win rate</th>
              <th scope="col">Net</th>
            </tr>
          </thead>
          <tbody>
            {classified.map((x) => (
              <tr key={x.regime} className={x.regime === s.weakest ? 'weak' : undefined}>
                <th scope="row">{x.regime.replace(/-/g, ' ')}</th>
                <td>{x.summary.tradeCount}</td>
                <td>{(Number(x.share) * 100).toFixed(0)}%</td>
                <td>{(Number(x.summary.winRate) * 100).toFixed(1)}%</td>
                <td className={Number(x.summary.netPnl) >= 0 ? 'up' : 'down'}>
                  {money(x.summary.netPnl)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {s.weakest && (
        // An observation with a number attached, not a recommendation. AEGIS
        // does not tell anyone to stop trading anything.
        <p className="scenario-note">
          Your worst results come in <strong>{s.weakest.replace(/-/g, ' ')}</strong> conditions.
        </p>
      )}

      {unclassified && unclassified.summary.tradeCount > 0 && (
        <p className="dim scenario-note">
          {unclassified.summary.tradeCount} trade
          {unclassified.summary.tradeCount === 1 ? '' : 's'} opened before the condition was
          being recorded, or while the feed was unavailable. They are counted here rather
          than dropped so the classified rows are not mistaken for the whole picture.
        </p>
      )}
    </section>
  );
}
